import { LogoField } from "@components/LogoField"
import { Button } from "@components/ui/Button"
import { ConfirmDialog } from "@components/ui/ConfirmDialog"
import { Field, SelectInput, TextInput } from "@components/ui/Field"
import { Modal, ModalActions } from "@components/ui/Modal"
import { useToast } from "@components/ui/Toaster"
import { zodResolver } from "@hookform/resolvers/zod"
import { useAcademicOverride } from "@hooks/useAcademicConfig"
import { toAcademicOverridePayload } from "@lib/academicConfig"
import { apiErrorCode, apiErrorStatus } from "@lib/apiError"
import { logger } from "@lib/logger"
import { useState } from "react"
import { useForm, type FieldErrors, type UseFormRegister } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  useCreateSchool,
  useRemoveSchoolLogo,
  useUpdateSchool,
  useUploadSchoolLogo,
} from "../hooks"
import { schoolSchema, type SchoolInput } from "../schemas"
import type { School } from "../types"

import { AcademicOverrideFields } from "./AcademicOverrideFields"

import { EDUCATION_LEVELS } from "@/constants/education"

const NO_OVERRIDE = { period_type: null, periods_per_cycle: null, grade_scale: null } as const

/** Name, place, CCT and level — the same fields on create and edit. */
function SchoolDetailsFields({
  register,
  errors,
  levelHint,
}: {
  register: UseFormRegister<SchoolInput>
  errors: FieldErrors<SchoolInput>
  levelHint: string
}) {
  const { t } = useTranslation("schools")
  const err = (message?: string) => (message ? t(`form.error.${message}`) : undefined)
  return (
    <>
      <Field label={t("fields.name")} required error={err(errors.name?.message)}>
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            autoComplete="off"
            placeholder={t("fields.name_placeholder")}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            {...register("name")}
          />
        )}
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("fields.city")} error={err(errors.city?.message)}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              autoComplete="off"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              {...register("city")}
            />
          )}
        </Field>
        <Field label={t("fields.state")} error={err(errors.state?.message)}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              autoComplete="off"
              aria-describedby={describedBy}
              aria-invalid={invalid}
              {...register("state")}
            />
          )}
        </Field>
      </div>

      <Field label={t("fields.cct")} hint={t("fields.cct_hint")} error={err(errors.cct?.message)}>
        {({ id, describedBy, invalid }) => (
          <TextInput
            id={id}
            autoComplete="off"
            className="sm:max-w-48"
            aria-describedby={describedBy}
            aria-invalid={invalid}
            {...register("cct")}
          />
        )}
      </Field>

      <Field
        label={t("fields.education_level")}
        required
        hint={levelHint}
        error={err(errors.education_level?.message)}
      >
        {({ id, describedBy, invalid }) => (
          <SelectInput
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            {...register("education_level")}
          >
            {EDUCATION_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t(`education_level.${level}`)}
              </option>
            ))}
          </SelectInput>
        )}
      </Field>
    </>
  )
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p
      role="alert"
      className="rounded-control border-danger bg-danger-soft text-danger-ink border px-3 py-2 text-sm"
    >
      {message}
    </p>
  )
}

/**
 * "Nueva escuela" (PRD-60 chunk B). The institution is the one in the header:
 * the form no longer asks for it again with a picker that could disagree.
 */
export function CreateSchoolDialog({
  institutionId,
  institutionName,
  onClose,
  onCreated,
}: {
  institutionId: string
  institutionName: string
  onClose: () => void
  onCreated?: (school: School) => void
}) {
  const { t } = useTranslation(["schools", "common", "errors"])
  const toast = useToast()
  const create = useCreateSchool()
  const academic = useAcademicOverride({ ...NO_OVERRIDE })
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SchoolInput>({
    resolver: zodResolver(schoolSchema),
    defaultValues: {
      name: "",
      city: "",
      state: "",
      cct: "",
      education_level: "highschool",
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    const override = academic.validate()
    if (!override) return
    setFormError(null)
    try {
      const school = await create.mutateAsync({
        ...values,
        institution_id: institutionId,
        ...toAcademicOverridePayload(override),
      })
      toast.success(t("create.success", { name: school.name }))
      logger.info("schools.created", { schoolId: school.id })
      onClose()
      onCreated?.(school)
    } catch (err) {
      const code = apiErrorCode(err)
      logger.error("schools.create.failed", { code, status: apiErrorStatus(err) })
      setFormError(t([`errors:${code}`, "errors:internal_error"]))
    }
  })

  return (
    <Modal open title={t("create.title")} onClose={onClose}>
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <p className="text-ink-muted text-sm">
          {t("create.intro", { institution: institutionName })}
        </p>
        <SchoolDetailsFields
          register={register}
          errors={errors}
          levelHint={t("fields.education_level_hint")}
        />
        <AcademicOverrideFields
          institutionId={institutionId}
          values={academic.values}
          onChange={academic.change}
          errorCode={academic.errorCode}
        />
        <FormError message={formError} />
        <ModalActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending}>
            {t("create.submit")}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  )
}

/**
 * A school's own settings, one form for two hosts (PRD-56 PR (4)): the
 * "Configuración" tab of `/escuelas/:id` and the wizard's edit dialog.
 * `school` comes from the live query, not a copy taken when the host
 * mounted: uploading a logo refetches, and the logo section has to show the
 * new crest and its "Quitar" at once.
 */
export function useEditSchoolForm(school: School, onSaved?: (saved: School) => void) {
  const { t } = useTranslation(["schools", "errors"])
  const toast = useToast()
  const update = useUpdateSchool()
  const [formError, setFormError] = useState<string | null>(null)
  // PRD-56 §FR-21 — the level rewrites every classroom of the school, so a
  // change is confirmed before it is sent.
  const [confirmingLevel, setConfirmingLevel] = useState(false)

  // PRD-38 §FR-11 — seeded from the STORED override columns, so a null
  // renders as "inherit" rather than as the resolved value.
  const academic = useAcademicOverride({
    period_type: school.period_type,
    periods_per_cycle: school.periods_per_cycle,
    grade_scale: school.grade_scale,
  })

  const form = useForm<SchoolInput>({
    resolver: zodResolver(schoolSchema),
    defaultValues: {
      name: school.name,
      city: school.city ?? "",
      state: school.state ?? "",
      cct: school.cct ?? "",
      education_level: school.education_level ?? "highschool",
    },
  })

  const save = async (values: SchoolInput) => {
    const override = academic.validate()
    if (!override) return
    setFormError(null)
    try {
      // All three override keys go on the wire every time, with an explicit
      // null for "inherit" — an omitted key would leave a stored override.
      const updated = await update.mutateAsync({
        id: school.id,
        data: { ...values, ...toAcademicOverridePayload(override) },
      })
      toast.success(t("edit.success", { name: updated.name }))
      logger.info("schools.updated", { schoolId: school.id })
      setConfirmingLevel(false)
      form.reset(values)
      onSaved?.(updated)
    } catch (err) {
      const code = apiErrorCode(err)
      logger.error("schools.update.failed", { code, status: apiErrorStatus(err) })
      setConfirmingLevel(false)
      setFormError(t([`errors:${code}`, "errors:internal_error"]))
    }
  }

  const submit = form.handleSubmit(async (values) => {
    if (values.education_level !== school.education_level && !confirmingLevel) {
      setConfirmingLevel(true)
      return
    }
    await save(values)
  })

  return {
    school,
    form,
    academic,
    submit,
    formError,
    isPending: update.isPending,
    confirmingLevel,
    cancelLevel: () => setConfirmingLevel(false),
  }
}

/** The school's own crest, or the institution's. Saves on upload. */
export function SchoolLogoField({
  school,
  institutionName,
  standalone,
}: {
  school: School
  institutionName: string
  standalone?: boolean
}) {
  const { t } = useTranslation(["schools", "errors"])
  const toast = useToast()
  const uploadLogo = useUploadSchoolLogo()
  const removeLogo = useRemoveSchoolLogo()

  async function handleLogo(action: "upload" | "remove", file?: File) {
    try {
      if (action === "upload" && file) {
        await uploadLogo.mutateAsync({ id: school.id, file })
        toast.success(t("branding.upload_success"))
      } else {
        await removeLogo.mutateAsync(school.id)
        toast.success(t("branding.remove_success", { institution: institutionName }))
      }
    } catch (err) {
      const code = apiErrorCode(err)
      toast.error(t([`errors:${code}`, "errors:internal_error"]))
      logger.error(`schools.logo_${action}.failed`, { code, status: apiErrorStatus(err) })
      throw err
    }
  }

  return (
    <LogoField
      logoUrl={school.logo_url}
      isOwnLogo={!!school.logo_path}
      inheritedFrom={institutionName}
      busy={uploadLogo.isPending || removeLogo.isPending}
      onUpload={(file) => handleLogo("upload", file)}
      onRemove={() => handleLogo("remove")}
      standalone={standalone}
    />
  )
}

export function EditSchoolFields({
  state,
  institutionName,
  withLogo = true,
}: {
  state: ReturnType<typeof useEditSchoolForm>
  institutionName: string
  /** A page shows the crest as its own section: it saves on its own. */
  withLogo?: boolean
}) {
  const { t } = useTranslation(["schools", "common"])
  const { school, form, academic } = state
  const level = form.watch("education_level")

  return (
    <>
      <SchoolDetailsFields
        register={form.register}
        errors={form.formState.errors}
        levelHint={t("fields.education_level_edit_hint")}
      />
      {withLogo && <SchoolLogoField school={school} institutionName={institutionName} />}
      <AcademicOverrideFields
        institutionId={school.institution_id}
        values={academic.values}
        onChange={academic.change}
        errorCode={academic.errorCode}
        defaultOpen={academic.hasOverride}
      />
      <FormError message={state.formError} />
      <ConfirmDialog
        open={state.confirmingLevel}
        title={t("edit.level_confirm.title", { name: school.name })}
        message={t("edit.level_confirm.message", {
          from: t(`education_level.${school.education_level}`),
          to: t(`education_level.${level}`),
        })}
        confirmLabel={t("edit.level_confirm.action")}
        tone="default"
        loading={state.isPending}
        onConfirm={() => void state.submit()}
        onClose={state.cancelLevel}
      />
    </>
  )
}

/** The wizard's in-place edit (PRD-60 chunk D); elsewhere a school is edited on its page. */
export function EditSchoolDialog({
  school,
  institutionName,
  onClose,
}: {
  school: School
  institutionName: string
  onClose: () => void
}) {
  const { t } = useTranslation(["schools", "common"])
  const state = useEditSchoolForm(school, onClose)

  return (
    <Modal open title={t("edit.title")} onClose={onClose}>
      <form onSubmit={state.submit} noValidate className="space-y-4">
        <EditSchoolFields state={state} institutionName={institutionName} />
        <ModalActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
          <Button type="submit" loading={state.isPending}>
            {t("edit.submit")}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  )
}
